import { EMPTY_LINK_SELECT_VALUE, UNSAFE_LINK_PROTOCOLS } from './constants';
import type {
  WysiwygEditorLinkTargetKind,
  WysiwygEditorOption,
  WysiwygLinkFieldValues,
  WysiwygLinkTargetAttrs,
} from './types';
import { escapeHtml } from './html';

const SUPPORTED_WEBSITE_LINK_PROTOCOLS = [
  'http:',
  'https:',
  'ftp:',
  'mailto:',
  'tel:',
];
const HOST_REQUIRED_WEBSITE_LINK_PROTOCOLS = ['http:', 'https:', 'ftp:'];
export type WysiwygLinkTargetOptions = Partial<
  Record<WysiwygEditorLinkTargetKind, WysiwygEditorOption[]>
>;
const FORM_OPTION_FIELD_BY_KIND: Partial<
  Record<WysiwygEditorLinkTargetKind, keyof WysiwygLinkFieldValues>
> = {
  'page-link': 'page-id',
  'item-link': 'item-id',
  'folder-link': 'folder-id',
  'app-link': 'app-id',
};
const TED_TYPE_BY_KIND: Partial<Record<WysiwygEditorLinkTargetKind, string>> = {
  'page-link': 'page',
  'item-link': 'item',
  'folder-link': 'category',
  'app-link': 'app',
};
const BARE_HOST_WITH_PORT_PATTERN = /^[^/:?#\s]+:\d+(?:[/?#].*)?$/;
const URL_SCHEME_PATTERN = /^([a-z][a-z0-9+.-]*):/i;

function getWebsiteUrlScheme(value: string) {
  const scheme = value.match(URL_SCHEME_PATTERN)?.[1] ?? '';

  return scheme ? `${scheme.toLowerCase()}:` : '';
}

function hasSupportedWebsiteUrlScheme(value: string) {
  return SUPPORTED_WEBSITE_LINK_PROTOCOLS.includes(getWebsiteUrlScheme(value));
}

function shouldPrefixWebsiteUrl(value: string) {
  return (
    Boolean(value) &&
    !hasSupportedWebsiteUrlScheme(value) &&
    (!getWebsiteUrlScheme(value) || BARE_HOST_WITH_PORT_PATTERN.test(value))
  );
}

export function normalizeWebsiteUrl(value: string) {
  const trimmed = value.trim();

  return shouldPrefixWebsiteUrl(trimmed) ? `https://${trimmed}` : trimmed;
}

export function isValidWebsiteUrl(value: string) {
  const normalizedUrl = normalizeWebsiteUrl(value);
  const url = getParsedUrl(normalizedUrl);

  return Boolean(
    normalizedUrl && url && isSupportedWebsiteLinkUrl(url, normalizedUrl)
  );
}

export function getOptionLabel(options: WysiwygEditorOption[], value: string) {
  return options.find((option) => option.value === value)?.label ?? value;
}

export function getOptionValue(
  options: WysiwygEditorOption[],
  value: string | null | undefined
) {
  if (value && options.some((option) => option.value === value)) {
    return value;
  }

  return EMPTY_LINK_SELECT_VALUE;
}

export function withPlaceholderOption(
  label: string,
  options: WysiwygEditorOption[]
): WysiwygEditorOption[] {
  return [{ value: EMPTY_LINK_SELECT_VALUE, label }, ...options];
}

export function getLinkTargetKindFromAttrs(
  attrs: Partial<WysiwygLinkTargetAttrs>
): WysiwygEditorLinkTargetKind {
  if (attrs.dataPage) return 'page-link';
  if (attrs.dataItem) return 'item-link';
  if (attrs.dataFolder) return 'folder-link';
  if (attrs.dataApp) return 'app-link';
  if (/^ted:\/\/page\//i.test(attrs.href ?? '')) return 'page-link';
  if (/^ted:\/\/item\//i.test(attrs.href ?? '')) return 'item-link';
  if (/^ted:\/\/category\//i.test(attrs.href ?? '')) return 'folder-link';
  if (/^ted:\/\/app\//i.test(attrs.href ?? '')) return 'app-link';
  if (attrs.href?.startsWith('#') && attrs.href.length > 1) {
    return 'anchor-link';
  }

  return 'website-link';
}

export function getComparableLinkFields(
  values: WysiwygLinkFieldValues,
  kind: WysiwygEditorLinkTargetKind
) {
  switch (kind) {
    case 'page-link':
      return {
        'folder-id': values['folder-id'],
        'page-id': values['page-id'],
        'link-text': values['link-text'].trim(),
      };
    case 'item-link':
      return {
        'folder-id': values['folder-id'],
        'page-id': values['page-id'],
        'item-id': values['item-id'],
        'link-text': values['link-text'].trim(),
      };
    case 'folder-link':
      return {
        'folder-id': values['folder-id'],
        'link-text': values['link-text'].trim(),
      };
    case 'app-link':
      return {
        'app-id': values['app-id'],
        'link-text': values['link-text'].trim(),
      };
    case 'anchor-link':
      return {
        'anchor-name': values['anchor-name'],
        'link-text': values['link-text'].trim(),
      };
    case 'website-link':
      return {
        'website-url': normalizeWebsiteUrl(values['website-url']),
        'link-text': values['link-text'].trim(),
      };
  }
}

export function getTedInternalLinkId(href: string, type: string) {
  const match = href.match(new RegExp(`^ted://${type}/([^?/#]+)`, 'i'));

  return match?.[1] ?? '';
}

export function getLinkTargetAttrs(
  kind: WysiwygEditorLinkTargetKind,
  values: WysiwygLinkFieldValues
): WysiwygLinkTargetAttrs {
  switch (kind) {
    case 'page-link':
      if (!values['page-id']) return getEmptyLinkTargetAttrs();

      return { href: '#', dataPage: values['page-id'] };
    case 'item-link':
      if (!values['item-id']) return getEmptyLinkTargetAttrs();

      return { href: `ted://item/${values['item-id']}` };
    case 'folder-link':
      if (!values['folder-id']) return getEmptyLinkTargetAttrs();

      return { href: `ted://category/${values['folder-id']}` };
    case 'app-link':
      if (!values['app-id']) return getEmptyLinkTargetAttrs();

      return { href: `ted://app/${values['app-id']}` };
    case 'anchor-link':
      if (!values['anchor-name']) return getEmptyLinkTargetAttrs();

      return { href: `#${values['anchor-name']}` };
    case 'website-link':
      return { href: normalizeWebsiteUrl(values['website-url']) };
  }
}

export function getEmptyLinkTargetAttrs(): WysiwygLinkTargetAttrs {
  return {
    href: '',
    target: null,
    dataPage: null,
    dataItem: null,
    dataFolder: null,
    dataApp: null,
  };
}

export function serializeLinkAttributes(attrs: WysiwygLinkTargetAttrs) {
  const attributes = [
    ['href', attrs.href],
    ['data-page', attrs.dataPage],
    ['data-item', attrs.dataItem],
    ['data-folder', attrs.dataFolder],
    ['data-app', attrs.dataApp],
  ].filter(([, value]) => value);

  return attributes
    .map(([name, value]) => `${name}="${escapeHtml(String(value))}"`)
    .join(' ');
}

export function getLinkTargetLabel(
  kind: WysiwygEditorLinkTargetKind,
  values: WysiwygLinkFieldValues | Partial<WysiwygLinkTargetAttrs>,
  targetOptions: WysiwygLinkTargetOptions
) {
  if (!('website-url' in values)) {
    return getStoredLinkTargetLabel(kind, values, targetOptions);
  }

  return getFormLinkTargetLabel(kind, values, targetOptions);
}

function getStoredLinkTargetLabel(
  kind: WysiwygEditorLinkTargetKind,
  values: Partial<WysiwygLinkTargetAttrs>,
  targetOptions: WysiwygLinkTargetOptions
) {
  const href = values.href ?? '';
  const optionValue = getStoredOptionValue(kind, values, href);
  const fallbackLabels: Partial<Record<WysiwygEditorLinkTargetKind, string>> = {
    'anchor-link': href.replace(/^#/, ''),
    'app-link': optionValue,
    'website-link': href,
  };

  return (
    getOptionBackedLinkLabel(kind, optionValue, targetOptions) ??
    fallbackLabels[kind] ??
    ''
  );
}

function getFormLinkTargetLabel(
  kind: WysiwygEditorLinkTargetKind,
  values: WysiwygLinkFieldValues,
  targetOptions: WysiwygLinkTargetOptions
) {
  const fallbackLabels: Partial<Record<WysiwygEditorLinkTargetKind, string>> = {
    'anchor-link': values['anchor-name'],
    'website-link': values['website-url'],
  };

  return (
    getOptionBackedLinkLabel(
      kind,
      getFormOptionValue(kind, values),
      targetOptions
    ) ??
    fallbackLabels[kind] ??
    ''
  );
}

function getParsedUrl(value: string) {
  try {
    return value ? new URL(value) : null;
  } catch {
    return null;
  }
}

function isSupportedWebsiteLinkUrl(url: URL, normalizedUrl: string) {
  const hasSupportedProtocol =
    SUPPORTED_WEBSITE_LINK_PROTOCOLS.includes(url.protocol) &&
    !UNSAFE_LINK_PROTOCOLS.includes(url.protocol);
  const hasRequiredTarget = HOST_REQUIRED_WEBSITE_LINK_PROTOCOLS.includes(
    url.protocol
  )
    ? Boolean(url.hostname)
    : normalizedUrl.replace(/^[a-z][a-z0-9+.-]*:/i, '').trim().length > 0;

  return hasSupportedProtocol && hasRequiredTarget;
}

function getStoredOptionValue(
  kind: WysiwygEditorLinkTargetKind,
  values: Partial<WysiwygLinkTargetAttrs>,
  href: string
) {
  const valuesByKind: Partial<Record<WysiwygEditorLinkTargetKind, string>> = {
    'page-link': values.dataPage ?? undefined,
    'item-link': values.dataItem ?? undefined,
    'folder-link': values.dataFolder ?? undefined,
    'app-link': values.dataApp ?? undefined,
  };
  const tedType = TED_TYPE_BY_KIND[kind];

  return (
    valuesByKind[kind] ?? (tedType ? getTedInternalLinkId(href, tedType) : '')
  );
}

function getFormOptionValue(
  kind: WysiwygEditorLinkTargetKind,
  values: WysiwygLinkFieldValues
) {
  const field = FORM_OPTION_FIELD_BY_KIND[kind];

  return field ? values[field] : '';
}

function getOptionBackedLinkLabel(
  kind: WysiwygEditorLinkTargetKind,
  value: string,
  targetOptions: WysiwygLinkTargetOptions
) {
  const options = targetOptions[kind];

  return options?.find((option) => option.value === value)?.label;
}
